import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { GraduationCap, Users, ArrowLeft } from "lucide-react";

export default function Signup() {
  const [role, setRole] = useState(null); // "teacher" | "family"
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const { setUser } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { ...form, role });
      setUser(data.user);
      toast.success("Hoş geldin! Profilini tamamlayalım.");
      nav(role === "teacher" ? "/ogretmen/kurulum" : "/aile/kurulum");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-lg mx-auto px-6 py-16">
        {!role ? (
          <div className="reveal">
            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-center tracking-tight" data-testid="signup-role-title">
              Nasıl kayıt olmak istiyorsun?
            </h1>
            <p className="mt-3 text-center text-neutral-500">Kayıt türüne göre profili senin için hazırlayacağız.</p>

            <div className="mt-12 grid gap-4 sm:grid-cols-2">
              <button
                onClick={() => setRole("teacher")}
                data-testid="signup-role-teacher"
                className="group border-2 border-slate-200 rounded-2xl p-8 text-left hover:border-[color:var(--brand)] hover:-translate-y-0.5 transition-all bg-white"
              >
                <div className="h-12 w-12 rounded-full bg-[color:var(--brand)]/10 text-[color:var(--brand)] grid place-items-center group-hover:bg-[color:var(--brand)] group-hover:text-white transition-colors">
                  <GraduationCap size={22} />
                </div>
                <div className="mt-6 font-display text-xl font-semibold">Öğretmen olarak</div>
                <div className="mt-2 text-sm text-slate-500">Ders vererek ailelere ulaş, öğrenci portföyünü büyüt.</div>
              </button>

              <button
                onClick={() => setRole("family")}
                data-testid="signup-role-family"
                className="group border-2 border-slate-200 rounded-2xl p-8 text-left hover:border-[color:var(--accent)] hover:-translate-y-0.5 transition-all bg-white"
              >
                <div className="h-12 w-12 rounded-full bg-[color:var(--accent)]/10 text-[color:var(--accent)] grid place-items-center group-hover:bg-[color:var(--accent)] group-hover:text-white transition-colors">
                  <Users size={22} />
                </div>
                <div className="mt-6 font-display text-xl font-semibold">Aile olarak</div>
                <div className="mt-2 text-sm text-slate-500">Çocuğuna uygun doğrulanmış öğretmeni bul.</div>
              </button>
            </div>

            <p className="mt-10 text-center text-sm text-neutral-500">
              Zaten hesabın var mı?{" "}
              <Link to="/giris/aile" className="font-medium text-neutral-900 underline underline-offset-4">Giriş Yap</Link>
            </p>
          </div>
        ) : (
          <div className="reveal">
            <button onClick={() => setRole(null)} className="text-sm text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1 mb-6" data-testid="signup-back">
              <ArrowLeft size={14} /> Geri
            </button>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight" data-testid="signup-form-title">
              {role === "teacher" ? "Öğretmen kaydı" : "Aile kaydı"}
            </h1>
            <p className="mt-3 text-neutral-500">Tüm alanları doldurup hesabını oluştur.</p>

            <form onSubmit={submit} className="mt-8 space-y-5">
              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-500 font-medium">Ad Soyad</Label>
                <Input required data-testid="signup-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-2 h-12 rounded-lg border-neutral-300 focus:border-neutral-900" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-500 font-medium">E-posta</Label>
                <Input required type="email" data-testid="signup-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-2 h-12 rounded-lg border-neutral-300 focus:border-neutral-900" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-500 font-medium">Telefon numarası</Label>
                <Input required type="tel" data-testid="signup-phone-input" placeholder="05XX XXX XX XX" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="mt-2 h-12 rounded-lg border-neutral-300 focus:border-neutral-900" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-neutral-500 font-medium">Şifre</Label>
                <Input required type="password" data-testid="signup-password-input" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="mt-2 h-12 rounded-lg border-neutral-300 focus:border-neutral-900" />
                <p className="mt-2 text-xs text-neutral-500">En az 6 karakter.</p>
              </div>

              <Button type="submit" disabled={loading} data-testid="signup-submit-btn"
                className="w-full h-12 rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white text-base shadow-md shadow-blue-500/20">
                {loading ? "Oluşturuluyor..." : "Hesap Oluştur"}
              </Button>
            </form>
          </div>
        )}
      </div>
    </Layout>
  );
}
